'use client';

import { LocationDisplay } from '@govinity/ui/components/meeting-bodies';
import { Badge } from '@govinity/ui/components/ui/badge';
import { Button } from '@govinity/ui/components/ui/button';
import { Card, CardHeader } from '@govinity/ui/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@govinity/ui/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@govinity/ui/components/ui/tooltip';
import { CopyToClipboardButton } from '@govinity/ui/components/ui-custom/copy-to-clipboard-button';
import {
  Calendar,
  FileText,
  Hash,
  Link,
  MapPin,
  PauseCircle,
  Phone,
  RadioOff,
  Share,
  Users,
  Video,
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useId } from 'react';
import { formatMeetingDateTime } from '@govinity/shared-config';
import { MeetingStatus } from '@govinity/validation';
import type { MeetingEventPublicGetResponse } from '@govinity/validation/public';
import { AgendaStatusBadge } from '@/components/ui/agenda-status-badge';
import { RECESS_BADGE_CLASS } from '@/lib/constants/recess';
import { cn } from '@/lib/utils';
import { resolveLiveBadge } from './lib/live-video-state';
import { PublicComments } from './public-comments';
import { PublicSpeakerSignup } from './public-speaker-signup';
import { useGovernmentContext } from '../../context';

type MeetingHeaderProps = {
  meeting: MeetingEventPublicGetResponse;
  hasVideo: boolean;
  hasMinutes: boolean;
  /**
   * Whether this live meeting is the one currently on air. When false the live badge becomes the
   * darker "Live - Off Air" treatment. Undefined while unknown (not live, or still resolving).
   */
  isOnAir?: boolean;
  /**
   * Whether this live meeting is in recess. Takes precedence over the off-air badge. Undefined while
   * unknown (not live, or still resolving).
   */
  isRecessed?: boolean;
  className?: string;
};

export function MeetingHeader({
  meeting,
  hasVideo,
  hasMinutes,
  isOnAir,
  isRecessed,
  className,
}: MeetingHeaderProps) {
  const t = useTranslations('MeetingHeader');
  const sharePopoverHeadingId = useId();
  const { government } = useGovernmentContext();
  const isCancelled = meeting.status === MeetingStatus.Cancelled;
  const {
    show: isLive,
    offAir: isLiveOffAir,
    recess: isRecess,
  } = resolveLiveBadge({
    status: meeting.status,
    isOnAir,
    isRecessed,
  });

  return (
    <Card className={cn('mb-4', className)}>
      <CardHeader>
        {/* Column-reverse below lg so the actions sit above the title without moving the heading
            out of reading order. */}
        <div className="flex flex-col-reverse gap-2 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex-1">
            <h1 className="text-foreground mb-2 text-2xl font-semibold">{meeting.title}</h1>

            <div className="text-muted-foreground mb-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
              <span className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4 shrink-0" aria-hidden="true" />
                {formatMeetingDateTime(meeting.date, government.timezone)}
              </span>
              {!meeting.cancelledAt &&
                (!!meeting.virtualMeetingUrl ||
                  !!meeting.meetingDialIn ||
                  !!meeting.meetingVirtualId) && (
                  <TooltipProvider>
                    {!!meeting.virtualMeetingUrl && (
                      <span className="text-muted-foreground inline-flex items-center gap-1">
                        <Link className="h-3 w-3 shrink-0" aria-hidden="true" />
                        <a
                          className="hover:text-primary hover:underline"
                          href={meeting.virtualMeetingUrl}
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          {t('virtualMeetingLink')}
                          <span className="sr-only"> {t('opensInNewTab')}</span>
                        </a>
                        <CopyToClipboardButton
                          className="h-4 w-4"
                          stringToCopy={meeting.virtualMeetingUrl}
                          tooltipText={t('copyVirtualMeetingLink')}
                        />
                      </span>
                    )}
                    {!!meeting.meetingDialIn && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-muted-foreground inline-flex items-center gap-1">
                            <Phone className="h-3 w-3 shrink-0" aria-hidden="true" />
                            <span className="sr-only">{t('meetingDialInLabel')}: </span>
                            {meeting.meetingDialIn}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>{t('meetingDialInLabel')}</TooltipContent>
                      </Tooltip>
                    )}
                    {!!meeting.meetingVirtualId && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-muted-foreground inline-flex items-center gap-1">
                            <Hash className="h-3 w-3 shrink-0" aria-hidden="true" />
                            <span className="sr-only">{t('meetingVirtualIdLabel')}: </span>
                            {meeting.meetingVirtualId}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>{t('meetingVirtualIdLabel')}</TooltipContent>
                      </Tooltip>
                    )}
                  </TooltipProvider>
                )}
            </div>
            {/* Inline metadata row */}
            <div className="text-muted-foreground mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <span className="flex items-center gap-1.5">
                <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
                <LocationDisplay
                  inline
                  fallback={meeting.location}
                  location={meeting.selectedLocation}
                />
              </span>
              <span className="flex items-center gap-1.5">
                <Users className="h-4 w-4 shrink-0" aria-hidden="true" />
                {meeting.meetingBodies.map((body) => body.name).join(', ')}
              </span>
            </div>

            {/* Status Badges */}
            <div className="flex flex-wrap items-center gap-2">
              {isCancelled && <Badge variant="danger-light">{t('cancelledBadge')}</Badge>}
              {!isCancelled && (
                <>
                  {isRecess && (
                    <Badge className={cn(RECESS_BADGE_CLASS, 'gap-1')}>
                      <PauseCircle className="h-3 w-3" aria-hidden="true" />
                      {t('recessBadge')}
                    </Badge>
                  )}
                  {isLive && !isRecess && (
                    <Badge variant={isLiveOffAir ? 'danger-dark' : 'danger'}>
                      {isLiveOffAir ? (
                        <RadioOff className="h-3 w-3" aria-hidden="true" />
                      ) : (
                        <span
                          className="mr-1 inline-flex h-2 w-2 animate-pulse rounded-full bg-white"
                          aria-hidden="true"
                        />
                      )}
                      {isLiveOffAir ? t('liveOffAirBadge') : t('liveBadge')}
                    </Badge>
                  )}
                  <AgendaStatusBadge meeting={meeting} />
                  <Badge variant={hasVideo ? 'info-alt2-light' : 'muted'}>
                    <Video className="h-3 w-3" aria-hidden="true" />
                    {hasVideo ? t('videoBadgeAvailable') : t('videoBadgeNotAvailable')}
                  </Badge>
                  <Badge variant={hasMinutes ? 'info-alt-light' : 'muted'}>
                    <FileText className="h-3 w-3" aria-hidden="true" />
                    {hasMinutes ? t('minutesBadgeAvailable') : t('minutesBadgeNotAvailable')}
                  </Badge>
                </>
              )}
            </div>
          </div>

          {/* Speaking sign-up, comment and share actions */}
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button size="sm" variant="outline">
                  <Share className="h-4 w-4" aria-hidden="true" />
                  {t('shareButton')}
                </Button>
              </PopoverTrigger>
              <PopoverContent aria-labelledby={sharePopoverHeadingId} className="w-56">
                <div className="space-y-2">
                  <p id={sharePopoverHeadingId} className="mb-3 text-sm font-semibold">
                    {t('sharePopoverHeading')}
                  </p>
                  <CopyToClipboardButton
                    className="w-full justify-start"
                    size="default"
                    stringToCopy={window.location.href}
                    variant="outline"
                    hideTooltip={true}
                  >
                    {t('copyLinkButton')}
                  </CopyToClipboardButton>
                </div>
              </PopoverContent>
            </Popover>
            <PublicSpeakerSignup meeting={meeting} />
            <PublicComments meeting={meeting} />
          </div>
        </div>
      </CardHeader>
    </Card>
  );
}
